import React, { useCallback, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { colors } from '../theme/designSystem';

/**
 * Área de anexar arquivo: clicar OU arrastar (RN-538c).
 *
 * A zona de soltar precisa ser VISÍVEL — quando ela existia só como comportamento invisível,
 * ninguém descobria que dava para arrastar. Fica acima da lista de anexos, porque abaixo se
 * afastaria conforme a lista cresce.
 *
 * `compact` encolhe a área para uma faixa: use quando já há arquivos e o espaço é da lista.
 */
interface FileDropzoneProps {
  onFile: (file: File) => void;
  accept?: string;
  disabled?: boolean;
  /** Aparece no lugar do convite quando a área está desabilitada — diga POR QUE. */
  disabledMessage?: string;
  /** Linha de apoio: formatos aceitos e limite de tamanho. */
  hint?: string;
  compact?: boolean;
}

const FileDropzone: React.FC<FileDropzoneProps> = ({
  onFile,
  accept,
  disabled = false,
  disabledMessage,
  hint,
  compact = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  // Contador de enter/leave: os filhos da área disparam dragleave e apagariam o realce
  const dragDepth = useRef(0);

  const pick = useCallback(
    (file: File | null | undefined) => {
      if (file && !disabled) {
        onFile(file);
      }
    },
    [onFile, disabled],
  );

  return (
    <Box
      onClick={() => !disabled && inputRef.current?.click()}
      onDragEnter={(e) => {
        e.preventDefault();
        dragDepth.current += 1;
        if (!disabled && e.dataTransfer.types.includes('Files')) setDragOver(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={(e) => {
        e.preventDefault();
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) setDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragOver(false);
        pick(e.dataTransfer.files?.[0]);
      }}
      sx={{
        border: `2px dashed ${dragOver ? colors.primary : colors.border}`,
        backgroundColor: dragOver ? 'rgba(3, 180, 198, 0.06)' : colors.background,
        borderRadius: '8px',
        padding: compact ? '0.75rem 1rem' : '1.5rem 1rem',
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color 120ms, background-color 120ms',
        '&:hover': disabled ? {} : { borderColor: colors.primary },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={accept}
        disabled={disabled}
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      {disabled && disabledMessage ? (
        <Typography sx={{ fontSize: '0.85rem', color: colors.textSecondary }}>
          {disabledMessage}
        </Typography>
      ) : (
        <>
          <CloudUpload
            sx={{
              fontSize: compact ? 22 : 36,
              color: colors.primary,
              verticalAlign: 'middle',
              mr: compact ? 1 : 0,
              mb: compact ? 0 : 0.5,
            }}
          />
          <Typography
            component={compact ? 'span' : 'p'}
            sx={{
              fontSize: compact ? '0.85rem' : '0.9rem',
              fontWeight: compact ? 400 : 600,
              color: compact ? colors.textSecondary : colors.text,
              verticalAlign: 'middle',
              m: 0,
            }}
          >
            Arraste um arquivo para cá ou clique para escolher
          </Typography>
          {hint && !compact && (
            <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, mt: 0.5 }}>
              {hint}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default FileDropzone;
